import { useCallback } from "react";
import { useLocation } from "react-router";

import { useScopedHref } from "@/hooks";

import { getLeagueNavItemPath, LeagueNavItem } from "./useLeagueNavGroups";

/**
 * Active when the current pathname matches the item's scoped href exactly or as a
 * path prefix — mirrors `useIsNavLinkActive` so the league sidebar highlights the
 * same way the normal sidebar does.
 *
 * Items targeting an Update page tab match `/edit/<editTabId>`; the `general` tab
 * is additionally active on the bare `/edit` default (no `tabId` segment), which
 * renders the General tab.
 */
export const useIsLeagueNavItemActive = (leagueId: string) => {
  const location = useLocation();
  const scopedHref = useScopedHref();
  const pathname = location.pathname;

  return useCallback(
    (item: LeagueNavItem) => {
      const itemPath = scopedHref(`/league/${leagueId}/${getLeagueNavItemPath(item)}`);
      if (pathname === itemPath || pathname.startsWith(`${itemPath}/`)) return true;

      if (item.editTabId === "general") {
        const bareEditPath = scopedHref(`/league/${leagueId}/edit`);
        return pathname === bareEditPath;
      }

      return false;
    },
    [scopedHref, leagueId, pathname]
  );
};
